/** Keeps link tokens (`?token=`) out of what PostHog sends: event URLs, person URLs, replay URLs. */
import { isPlainObject, omit } from 'es-toolkit';
import type { CaptureResult, CapturedNetworkRequest, PostHogConfig, Properties } from 'posthog-js';

const TOKEN_PARAM = 'token';
const REDACTED = 'redacted';

/** A `token` query parameter, plain (`?token=…`) or percent-encoded inside another URL. */
const TOKEN_PATTERNS: readonly RegExp[] = [
  new RegExp(`([?&]${TOKEN_PARAM}=)[^&#\\s"'<>]+`, 'g'),
  new RegExp(`((?:%3F|%26)${TOKEN_PARAM}%3D)[^%&\\s"'<>]+`, 'gi')
];

/** `text` with the value of every `token` query parameter in it replaced; the rest is kept. */
export function redactTokenParam(text: string): string {
  return TOKEN_PATTERNS.reduce((result, pattern) => result.replace(pattern, `$1${REDACTED}`), text);
}

/** Replay snapshots: their URLs are masked by the replay callback, and walking a DOM tree is costly. */
const SKIPPED_KEYS: ReadonlySet<string> = new Set(['$snapshot_data']);

/** Every string, key included (heatmap data is keyed by URL), redacted at any depth. */
function redactDeep(value: unknown): unknown {
  if (typeof value === 'string') return redactTokenParam(value);
  if (Array.isArray(value)) return value.map(redactDeep);
  if (!isPlainObject(value)) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      redactTokenParam(key),
      SKIPPED_KEYS.has(key) ? entry : redactDeep(entry)
    ])
  );
}

/** $current_url, $referrer, $initial_current_url, $session_entry_url… and anything nested. */
function redactValues(properties: Properties): Properties {
  // Safe: redactDeep keeps the shape and only rewrites strings, so properties stay properties.
  return redactDeep(properties) as Properties;
}

/** PostHog `before_send`: the event with every URL it carries redacted. */
export function redactAnalyticsEvent(event: CaptureResult | null): CaptureResult | null {
  if (event === null) return null;
  return {
    ...event,
    properties: redactValues(event.properties),
    ...(event.$set && { $set: redactValues(event.$set) }),
    ...(event.$set_once && { $set_once: redactValues(event.$set_once) })
  };
}

/**
 * PostHog `session_recording.maskCapturedNetworkRequestFn`: replay URLs redacted, bodies dropped —
 * a callback here replaces the SDK's own body scrubbing, so no body is ever kept.
 */
export function redactRecordedRequest(request: CapturedNetworkRequest): CapturedNetworkRequest {
  return {
    ...omit(request, ['requestBody', 'responseBody']),
    name: redactTokenParam(request.name)
  };
}

/**
 * The PostHog options that keep `token` out: masked at the source (stored person info, `/flags`,
 * replay, heatmaps), then redacted once more in every event before it is sent.
 */
export const ANALYTICS_PRIVACY_CONFIG = {
  // Also masks ad click ids (gclid, fbclid…); custom keys apply only with this switch on.
  mask_personal_data_properties: true,
  custom_personal_data_properties: [TOKEN_PARAM],
  before_send: redactAnalyticsEvent,
  // Bodies carry passwords and link tokens; the callback alone would also turn off their scrubbing.
  session_recording: { recordBody: false, maskCapturedNetworkRequestFn: redactRecordedRequest }
} satisfies Partial<PostHogConfig>;
